import xtreamAPI from '../api/xtreamAPI';
import {
  fetchMovieDetails,
  fetchTvDetails,
  sanitizeTitleForSearch,
  searchMovieIdentityCandidates,
  searchSeriesIdentityCandidates,
  TmdbIdentityCandidate,
  TmdbMovieDetailsResult,
  TmdbTvDetailsResult,
} from '../api/tmdbAPI';

export type ContentIdentityType = 'movie' | 'series';

export interface ResolveContentIdentityInput {
  type: ContentIdentityType;
  streamId?: string;
  entryTmdbId?: number;
  xtreamTmdbId?: number;
  title?: string;
  year?: string;
  plot?: string;
}

export interface ResolvedContentIdentity {
  tmdbId: number;
  source: 'entry' | 'xtream' | 'cache' | 'search' | 'none';
  details: TmdbMovieDetailsResult | TmdbTvDetailsResult | null;
}

interface IdentityCacheRecord {
  tmdbId: number;
  expiresAt: number;
  retryAfter?: number;
}

const CACHE_PREFIX = 'ott_content_identity_v1_';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const FAILURE_RETRY_MS = 2 * 60 * 1000;

function normalizeTitle(value: string): string {
  return sanitizeTitleForSearch(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractYear(value?: string): number | null {
  const match = String(value || '').match(/(?:19|20)\d{2}/);
  return match ? Number(match[0]) : null;
}

function bigramSimilarity(left: string, right: string): number {
  const a = normalizeTitle(left);
  const b = normalizeTitle(right);
  if (!a || !b) return 0;
  if (a === b) return 1;
  const counts = (value: string): Map<string, number> => {
    const result = new Map<string, number>();
    for (let index = 0; index < value.length - 1; index += 1) {
      const pair = value.slice(index, index + 2);
      result.set(pair, (result.get(pair) || 0) + 1);
    }
    return result;
  };
  const aPairs = counts(a);
  const bPairs = counts(b);
  let intersection = 0;
  let aTotal = 0;
  let bTotal = 0;
  aPairs.forEach((count) => { aTotal += count; });
  bPairs.forEach((count) => { bTotal += count; });
  aPairs.forEach((count, pair) => { intersection += Math.min(count, bPairs.get(pair) || 0); });
  return aTotal + bTotal ? (2 * intersection) / (aTotal + bTotal) : 0;
}

/** Pure selection rule, exported so ambiguity and confidence stay regression-tested. */
export function selectTrustedIdentityCandidate(
  candidates: TmdbIdentityCandidate[],
  title: string,
  year?: string,
  plot?: string,
): TmdbIdentityCandidate | null {
  const wantedTitle = normalizeTitle(title);
  if (!wantedTitle) return null;
  const exact = candidates.filter((candidate) => {
    return normalizeTitle(candidate.title) === wantedTitle ||
      normalizeTitle(candidate.originalTitle) === wantedTitle;
  });
  if (!exact.length) return null;

  const wantedYear = extractYear(year);
  const scored = exact.map((candidate) => {
    const candidateYear = extractYear(candidate.date);
    const yearDistance = wantedYear && candidateYear ? Math.abs(wantedYear - candidateYear) : null;
    const plotScore = plot && candidate.overview ? bigramSimilarity(plot, candidate.overview) : 0;
    const yearScore = yearDistance === 0 ? 3 : yearDistance === 1 ? 2 : 0;
    return { candidate, yearDistance, plotScore, score: yearScore + plotScore * 4 };
  });

  if (scored.length === 1) {
    const only = scored[0];
    if (only.yearDistance !== null && only.yearDistance > 1 && only.plotScore < 0.55) return null;
    return only.candidate;
  }

  scored.sort((left, right) => right.score - left.score);
  const best = scored[0];
  const second = scored[1];
  const hasYearSignal = best.yearDistance !== null && best.yearDistance <= 1;
  const hasPlotSignal = best.plotScore >= 0.35;
  if (!hasYearSignal && !hasPlotSignal) return null;
  if (best.score - second.score < 0.35) return null;
  return best.candidate;
}

function cacheKey(type: ContentIdentityType, streamId: string): string {
  const host = xtreamAPI.getHost();
  return `${CACHE_PREFIX}${encodeURIComponent(host)}_${type}_${streamId}`;
}

function readCache(key: string): IdentityCacheRecord | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IdentityCacheRecord;
    if (!parsed.expiresAt || Date.now() >= parsed.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(key: string, record: IdentityCacheRecord): void {
  try { localStorage.setItem(key, JSON.stringify(record)); } catch { /* opportunistic cache */ }
}

async function fetchDetails(type: ContentIdentityType, tmdbId: number) {
  return type === 'movie' ? fetchMovieDetails(tmdbId) : fetchTvDetails(tmdbId);
}

export async function resolveContentIdentity(
  input: ResolveContentIdentityInput,
): Promise<ResolvedContentIdentity> {
  const streamId = String(input.streamId || '');
  const key = streamId ? cacheKey(input.type, streamId) : '';
  const cached = key ? readCache(key) : null;
  if (cached?.retryAfter && Date.now() < cached.retryAfter) {
    return { tmdbId: 0, source: 'none', details: null };
  }

  const candidates: Array<{ id: number; source: ResolvedContentIdentity['source'] }> = [];
  if (input.entryTmdbId) candidates.push({ id: input.entryTmdbId, source: 'entry' });
  if (input.xtreamTmdbId && input.xtreamTmdbId !== input.entryTmdbId) {
    candidates.push({ id: input.xtreamTmdbId, source: 'xtream' });
  }
  if (cached?.tmdbId && !candidates.some((candidate) => candidate.id === cached.tmdbId)) {
    candidates.push({ id: cached.tmdbId, source: 'cache' });
  }

  for (const candidate of candidates) {
    try {
      const details = await fetchDetails(input.type, candidate.id);
      if (key) writeCache(key, { tmdbId: candidate.id, expiresAt: Date.now() + CACHE_TTL_MS });
      return { tmdbId: candidate.id, source: candidate.source, details };
    } catch {
      // Invalid or stale ids fall through to the trusted title search.
    }
  }

  try {
    const title = input.title || '';
    const searchResults = input.type === 'movie'
      ? await searchMovieIdentityCandidates(title)
      : await searchSeriesIdentityCandidates(title);
    const trusted = selectTrustedIdentityCandidate(searchResults, title, input.year, input.plot);
    if (trusted) {
      const details = await fetchDetails(input.type, trusted.id);
      if (key) writeCache(key, { tmdbId: trusted.id, expiresAt: Date.now() + CACHE_TTL_MS });
      return { tmdbId: trusted.id, source: 'search', details };
    }
  } catch {
    // Keep Xtream details usable and retry after a short cooldown.
  }

  if (key) {
    writeCache(key, {
      tmdbId: cached?.tmdbId || 0,
      retryAfter: Date.now() + FAILURE_RETRY_MS,
      expiresAt: Date.now() + FAILURE_RETRY_MS,
    });
  }
  return { tmdbId: 0, source: 'none', details: null };
}

export function clearContentIdentityCache(host?: string): void {
  const scopedPrefix = host ? `${CACHE_PREFIX}${encodeURIComponent(host)}_` : CACHE_PREFIX;
  Object.keys(localStorage)
    .filter((key) => key.startsWith(scopedPrefix))
    .forEach((key) => localStorage.removeItem(key));
}

export const CONTENT_IDENTITY_CACHE_PREFIX = CACHE_PREFIX;
